import React, { useState, useEffect, useCallback } from 'react';
import { getList, generateList, addToList, removeFromList, updateListItem, submitOrder, getPendingOrders, confirmOrder, clearList } from '../api';
import { TrashIcon, ShoppingCartIcon, TruckIcon, BuildingStorefrontIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { SkeletonList } from '../components/Skeleton';
import BarcodeScanner from '../components/BarcodeScanner';
import ItemSearch from '../components/ItemSearch';

export default function Dashboard({ showToast, onInStore, onNavigate }) {
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [showScanner, setShowScanner] = useState(false);

  const loadList = useCallback(async () => {
    try {
      const data = await getList();
      setList(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const loadPending = useCallback(async () => {
    try {
      const data = await getPendingOrders();
      setPendingOrders(data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadList(); loadPending(); }, [loadList, loadPending]);

  const handleScanResult = async (result) => {
    setShowScanner(false);
    if (result.found && result.source === 'local') {
      // Item exists in DB — add directly to list
      try {
        await addToList(result.item._id, result.item.defaultQuantity || 1);
        loadList();
        showToast(`Added ${result.item.friendlyName} to list`);
      } catch (e) {
        showToast('Error adding item to list');
      }
    } else {
      // Not in DB — redirect to Settings to create it
      showToast('Item not in database — redirecting to Settings to create it');
      if (onNavigate) onNavigate('settings');
    }
  };

  const handleAddItem = async (item) => {
    await addToList(item._id, item.defaultQuantity || 1);
    loadList();
    showToast(`Added ${item.friendlyName}`);
  };

  const handleRemoveItem = async (index) => {
    await removeFromList(index);
    loadList();
  };

  const handleGenerate = async () => {
    await generateList();
    loadList();
    showToast('List generated from pantry & staples');
  };

  const handleClear = async () => {
    if (!activeItems.length) { showToast('List is already empty'); return; }
    await clearList();
    loadList();
    showToast('Shopping list cleared');
  };

  const handleInStore = async () => {
    await submitOrder('in-store');
    onInStore(list);
    loadList();
    loadPending();
  };

  const handleDeliveryPickup = async (mode) => {
    if (!list || !activeItems.length) { showToast('List is empty'); return; }
    try {
      await submitOrder(mode);
      loadList();
      loadPending();
      const label = mode === 'delivery' ? '🚚 Delivery' : '🛒 Pickup';
      showToast(`${label} order submitted! Clank will handle it.`);
    } catch {
      showToast('Failed to submit order');
    }
  };

  const handleConfirmOrder = async (id) => {
    await confirmOrder(id);
    loadPending();
    showToast('✅ Order confirmed, pantry updated!');
  };

  const activeItems = list?.items?.filter(i => i.itemId) || [];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">Weekly Shopping List</h2>
        <div className="flex gap-2">
          <button onClick={handleClear} className="text-sm bg-gray-700 hover:bg-gray-600 px-3 py-1.5 rounded-lg text-red-400">
            Clear
          </button>
          <button onClick={handleGenerate} className="text-sm bg-gray-700 hover:bg-gray-600 px-3 py-1.5 rounded-lg">
            Auto-Generate
          </button>
        </div>
      </div>

      {/* Action buttons - always at top */}
      <div className="flex gap-3 mb-4">
        <button
          onClick={handleInStore}
          disabled={activeItems.length === 0}
          className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-500 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2"
        >
          <BuildingStorefrontIcon className="w-5 h-5" />
          <span>In-Store</span>
        </button>
        <button
          onClick={() => handleDeliveryPickup('delivery')}
          disabled={activeItems.length === 0}
          className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:text-gray-500 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2"
        >
          <TruckIcon className="w-5 h-5" />
          <span>Delivery</span>
        </button>
        <button
          onClick={() => handleDeliveryPickup('pickup')}
          disabled={activeItems.length === 0}
          className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:text-gray-500 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2"
        >
          <ShoppingCartIcon className="w-5 h-5" />
          <span>Pickup</span>
        </button>
      </div>

      {/* Add item */}
      <div className="mb-4">
          <ItemSearch
            onSelect={handleAddItem}
            placeholder="+ Add item (type to search)..."
            actionIcon="add"
            rightElement={
              <button
                onClick={() => setShowScanner(true)}
                className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-3 rounded-xl flex items-center gap-2 font-medium shrink-0"
                title="Scan barcode"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
                </svg>
              </button>
            }
          />
        </div>

        {/* List items */}
        {loading ? (
          <SkeletonList count={4} />
        ) : activeItems.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <ShoppingCartIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>List is empty</p>
            <p className="text-sm mt-1">Add items or tap Auto-Generate</p>
          </div>
        ) : (
          <div className="space-y-2">
            {activeItems.map((item, idx) => (
              <div key={idx} className="bg-gray-800 rounded-xl px-4 py-3 flex items-center justify-between border border-gray-700">
                <div className="flex-1">
                  <span className="font-medium">{item.itemId.friendlyName}</span>
                  {item.itemId.variant && <span className="text-gray-400 text-sm ml-2">({item.itemId.variant})</span>}
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1 bg-gray-700 rounded-lg">
                    <button
                      onClick={() => { if (item.quantity > 1) updateListItem(idx, { quantity: item.quantity - 1 }).then(loadList); }}
                      className="px-2 py-1 text-gray-300 hover:text-white"
                    >−</button>
                    <span className="px-2 min-w-[2rem] text-center">{item.quantity}</span>
                    <button
                      onClick={() => updateListItem(idx, { quantity: item.quantity + 1 }).then(loadList)}
                      className="px-2 py-1 text-gray-300 hover:text-white"
                    >+</button>
                  </div>
                  <button onClick={() => handleRemoveItem(idx)} className="text-gray-500 hover:text-red-400 p-1">
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pending orders */}
        {pendingOrders.length > 0 && (
          <div className="mt-8">
            <h3 className="text-lg font-semibold mb-3 text-yellow-400">📦 Pending Orders</h3>
            {pendingOrders.map(order => (
              <div key={order._id} className="bg-gray-800 border border-yellow-600/30 rounded-xl p-4 mb-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">
                    {order.mode} — {new Date(order.orderedAt).toLocaleDateString()}
                  </span>
                  <span className="text-xs bg-yellow-600/20 text-yellow-400 px-2 py-1 rounded">Pending</span>
                </div>
                <div className="text-sm text-gray-300 mb-3">
                  {order.items.filter(i => i.itemId).map((i, idx) => (
                    <span key={idx}>{i.itemId.friendlyName} ×{i.quantity}{idx < order.items.length - 1 ? ', ' : ''}</span>
                  ))}
                </div>
                <button
                  onClick={() => handleConfirmOrder(order._id)}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2"
                >
                  <CheckCircleIcon className="w-5 h-5" />
                  {order.mode === 'in-store' ? 'Order Picked Up' : 'Order Received'}
                </button>
              </div>
            ))}
          </div>
        )}

      {/* Barcode Scanner */}
      {showScanner && (
        <BarcodeScanner
          onResult={handleScanResult}
          onClose={() => setShowScanner(false)}
        />
      )}
    </div>
  );
}
